import jilOrganizationsProductsLicenseGroups from 'api/jil/jilOrganizationsProductsLicenseGroups';
import {MEMBER_EVENT} from 'models/member/MemberConstants';
import {MEMBER_TECH_ACCOUNT_DOMAIN} from 'models/member/type/MemberTypeConstants';
import OrganizationUser from 'models/organizationUser/OrganizationUser';
import modelCache from 'services/cache/modelCache/modelCache';
import eventBus from 'services/events/eventBus';
import feature from 'services/feature';
import log from 'services/log';
import MemberList from 'services/member/MemberList';
import UserGroup from 'services/users/user-group/UserGroup';

import {
  LICENSE_GROUP_USER_LIST_CACHE_ID,
  LICENSE_GROUP_USER_LIST_EVENT,
} from './LicenseGroupUserListConstants';

class LicenseGroupUserList extends MemberList {
  constructor(options) {
    super({
      filterExcludeDomain: MEMBER_TECH_ACCOUNT_DOMAIN,
      itemClassRef: [OrganizationUser, UserGroup],
      modelCacheId: LICENSE_GROUP_USER_LIST_CACHE_ID,
      refreshResource: jilOrganizationsProductsLicenseGroups.getLicenseGroupUserList,
      saveResource: jilOrganizationsProductsLicenseGroups.patchLicenseGroupUserList,
      transformResponseData,
      ...options,
    });
    this.licenseGroupId = options.licenseGroupId;
    this.productId = options.productId;
  }

  /**
   * @description overidden key method to be used when we fetch models from modelCache.
   * @deprecated Remove with temp_src2_cache
   *
   * @returns {String} key formed from local params and JilModelList params.
   */
  getKey() {
    if (feature.isEnabled('temp_src2_cache')) {
      return super.getKey();
    }
    return super.getKey(getParams(this));
  }

  /**
   * @description Get params for api call to fetch list.
   *
   * @returns {Object} params to be passed to query
   */
  getQueryParams() {
    return {
      ...super.getQueryParams(),
      licenseGroupId: this.licenseGroupId,
      productId: this.productId,
    };
  }

  /**
   * @description Method to execute after a successful refresh.
   */
  onRefreshSuccess() {
    eventBus.emit(LICENSE_GROUP_USER_LIST_EVENT.UPDATE.LIST, this.licenseGroupId);
    if (this.shouldUpdateTotalItemCount()) {
      eventBus.emit(LICENSE_GROUP_USER_LIST_EVENT.UPDATE.COUNT, this.pagination.itemCount);
    }
  }

  /**
   * @description Method to refresh the current model state against what is stored in the back-end.
   * @deprecated Remove with temp_src2_cache
   *
   * @param {Object} options - options for refresh method of super class
   * @return {Promise} resolves to refreshed ProductConfigurationUserList model, else rejects with error message
   */
  async refresh(options = {}) {
    if (feature.isEnabled('temp_src2_cache')) {
      return super.refresh();
    }

    try {
      await super.refresh(
        {
          licenseGroupId: this.licenseGroupId,
          orgId: this.orgId,
          productId: this.productId,
        },
        options
      );
    } catch (error) {
      log.error('LicenseGroupUserList.refresh() failed. Error: ', error);
      return Promise.reject(error);
    }

    eventBus.emit(LICENSE_GROUP_USER_LIST_EVENT.UPDATE.LIST, this.licenseGroupId);
    if (this.shouldUpdateTotalItemCount()) {
      eventBus.emit(LICENSE_GROUP_USER_LIST_EVENT.UPDATE.COUNT, this.pagination.itemCount);
    }
    return this;
  }

  /**
   * @description Method to save changes to license group list to back-end.
   * @returns {Promise} resolves if changes successfully saved, else rejects with error message.
   */
  save() {
    return super.save(getParams(this), {refresh: false});
  }

  /**
   * @description Method to update the role for a set of members in the list on the back-end.
   * @param {Array} members - members within this list to perform the operation on
   * @param {Number} role - the role to assign to those members for this list
   * @returns {Promise} resolves if changes successfully saved, else rejects with error message
   */
  async saveMemberProductRole(members) {
    const operations = getSaveOperations(members);

    try {
      await jilOrganizationsProductsLicenseGroups.patchLicenseGroupUserList({
        licenseGroupId: this.licenseGroupId,
        operations,
        orgId: this.orgId,
        productId: this.productId,
      });
    } catch (error) {
      log.error('Failed to update role for selected users in configuration. Error: ', error);
      return Promise.reject(error);
    }

    // Since a member's productRole isn't (and can't easily be) reflected in the cache key,
    // if a productRole is modified make sure the next 'get' will not use a stale cache entry.
    modelCache.clear(this.modelCacheId);

    members.forEach((member) => {
      eventBus.emit(MEMBER_EVENT.UPDATE, member.id);
    });
    return this;
  }
}

function getSaveOperations(members) {
  return members.map((member) => ({
    op: 'replace',
    path: `/${member.id}/${member.productRole}`,
  }));
}

function transformResponseData(responseData, classRefs) {
  return responseData.map((responseItem) => {
    const ClassRef = classRefs.find((ref) => ref.canTransform(responseItem));
    return new ClassRef(responseItem);
  });
}

///////////

/**
 * @description Get params used for API calls.
 *              Rename to getSaveParams when we remove temp_src2_cache.
 *
 * @param {Object} model the model to pick params from.
 * @returns {Array} params to be passed to JIL or used as a key.
 */
function getParams(model) {
  return {
    licenseGroupId: model.licenseGroupId,
    ...(feature.isEnabled('temp_src2_cache') ? {} : {orgId: model.orgId}),
    productId: model.productId,
  };
}

export default LicenseGroupUserList;
